import { useMemo, useRef, useState } from "react";
import ClassNames from "Helpers/Common";
import "Colors.css";

// Stripe-style date range picker: one button opens a popover with preset
// shortcuts, a dual-month calendar, and Clear/Apply. Commits only on Apply
// via onChange({ from, to }) — both "YYYY-MM-DD" strings, or empty strings
// for "All time".

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const toYMD = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fromYMD = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const fmtShort = (d) =>
  d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const sameDay = (a, b) =>
  a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const PRESETS = [
  { label: "Today", range: (now) => [now, now] },
  { label: "Last 7 days", range: (now) => [new Date(now.getTime() - 6 * DAY_MS), now] },
  { label: "Last 4 weeks", range: (now) => [new Date(now.getTime() - 27 * DAY_MS), now] },
  { label: "Last 6 months", range: (now) => [new Date(now.getFullYear(), now.getMonth() - 6, now.getDate()), now] },
  { label: "Last 12 months", range: (now) => [new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()), now] },
  { label: "Month to date", range: (now) => [new Date(now.getFullYear(), now.getMonth(), 1), now] },
  { label: "Quarter to date", range: (now) => [new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1), now] },
  { label: "Year to date", range: (now) => [new Date(now.getFullYear(), 0, 1), now] },
  { label: "All time", range: () => [null, null] },
];

const MonthGrid = ({ year, month, from, to, onDayClick }) => {
  const weeks = useMemo(() => {
    const first = new Date(year, month, 1);
    const days = [];
    for (let i = 0; i < first.getDay(); i++) days.push(null);
    const last = new Date(year, month + 1, 0).getDate();
    for (let d = 1; d <= last; d++) days.push(new Date(year, month, d));
    const rows = [];
    for (let i = 0; i < days.length; i += 7) rows.push(days.slice(i, i + 7));
    return rows;
  }, [year, month]);

  const inRange = (d) => from && to && d > from && d < to;

  return (
    <table className="text-sm select-none" style={{ borderCollapse: "collapse" }}>
      <thead>
        <tr>
          {WEEKDAYS.map((w) => (
            <th key={w} className="w-9 h-8 font-medium bali_hai text-center">{w}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {weeks.map((week, i) => (
          <tr key={i}>
            {week.map((d, j) => (
              <td key={j} className="p-0 text-center">
                {d && (
                  <button
                    type="button"
                    onClick={() => onDayClick(d)}
                    className={ClassNames(
                      "w-9 h-9 text-sm focus:outline-none",
                      sameDay(d, from) || sameDay(d, to)
                        ? "bg-blue-chill text-white rounded-full font-semibold"
                        : inRange(d)
                          ? "bg-blue-100 text-gray-800"
                          : "text-gray-700 hover:bg-gray-100 rounded-full"
                    )}
                  >
                    {d.getDate()}
                  </button>
                )}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export function DateRangePicker({ from, to, onChange }) {
  const [open, setOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(null);
  const [draftTo, setDraftTo] = useState(null);
  // Fixed-position anchor for the popover: the page content area scrolls
  // inside an overflow-hidden flex column, which clips absolutely-positioned
  // children — position:fixed escapes every overflow ancestor.
  const btnRef = useRef(null);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });
  // First month shown; second calendar is viewMonth + 1.
  const [view, setView] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() - 1, 1);
  });
  const today = useRef(startOfDay(new Date())).current;

  const openPicker = () => {
    setDraftFrom(fromYMD(from));
    setDraftTo(fromYMD(to));
    const r = btnRef.current.getBoundingClientRect();
    setPanelPos({
      top: r.bottom + 8,
      left: Math.max(16, Math.min(r.right - 800, window.innerWidth - 816)),
    });
    setOpen(true);
  };

  const onDayClick = (d) => {
    if (!draftFrom || (draftFrom && draftTo)) {
      setDraftFrom(d);
      setDraftTo(null);
    } else if (d < draftFrom) {
      setDraftFrom(d);
    } else {
      setDraftTo(d);
    }
  };

  const applyPreset = (preset) => {
    const [f, t] = preset.range(today);
    setDraftFrom(f);
    setDraftTo(t);
    if (f) setView(new Date(f.getFullYear(), f.getMonth(), 1));
  };

  const apply = () => {
    onChange({
      from: draftFrom ? toYMD(draftFrom) : "",
      to: draftTo ? toYMD(draftTo) : draftFrom ? toYMD(draftFrom) : "",
    });
    setOpen(false);
  };

  const label = from && to
    ? from === to
      ? fmtShort(fromYMD(from))
      : `${fmtShort(fromYMD(from))} → ${fmtShort(fromYMD(to))}`
    : "All time";

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => (open ? setOpen(false) : openPicker())}
        className="flex items-center gap-2 h-9 px-3 rounded-md border border-gray-300 shadow-sm bg-white text-xs focus:outline-none whitespace-nowrap"
      >
        <span className="bali_hai">Date range</span>
        <span className="text-blue-chill font-medium">{label}</span>
        <svg viewBox="0 0 20 20" className="h-4 w-4 text-gray-400" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </button>

      {open && (
        <>
          {/* click-away backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="fixed z-50 bg-white rounded-xl shadow-2xl border border-gray-200 p-6 flex gap-6"
            style={{
              top: `${panelPos.top}px`,
              left: `${panelPos.left}px`,
              width: "800px",
              maxWidth: "calc(100vw - 32px)",
              maxHeight: `calc(100vh - ${panelPos.top + 16}px)`,
              overflowY: "auto",
            }}
          >
            <div className="flex flex-col gap-0.5 w-40 flex-shrink-0 border-r border-gray-100 pr-3">
              {PRESETS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => applyPreset(p)}
                  className="text-left text-sm px-3 py-1.5 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none"
                >
                  {p.label}
                </button>
              ))}
            </div>

            <div className="flex-1">
              <div className="flex items-center gap-3 mb-5">
                <span className="text-sm bali_hai">Start</span>
                <div className="h-10 px-4 flex items-center rounded-lg border border-gray-300 text-sm text-gray-800" style={{ minWidth: "130px" }}>
                  {draftFrom ? fmtShort(draftFrom) : "—"}
                </div>
                <span className="text-sm bali_hai">End</span>
                <div className="h-10 px-4 flex items-center rounded-lg border border-gray-300 text-sm text-gray-800" style={{ minWidth: "130px" }}>
                  {draftTo ? fmtShort(draftTo) : draftFrom ? fmtShort(draftFrom) : "—"}
                </div>
              </div>

              <div className="flex gap-8">
                {[0, 1].map((offset) => {
                  const m = new Date(view.getFullYear(), view.getMonth() + offset, 1);
                  const NavArrow = ({ dir }) => (
                    <button
                      type="button"
                      onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + dir, 1))}
                      className="h-8 w-8 flex items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus:outline-none"
                    >
                      <svg viewBox="0 0 20 20" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                        {dir < 0 ? (
                          <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.94 10l3.83 3.71a.75.75 0 11-1.04 1.08l-4.39-4.25a.75.75 0 010-1.08l4.39-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                        ) : (
                          <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.06 10 7.23 6.29a.75.75 0 111.04-1.08l4.39 4.25a.75.75 0 010 1.08l-4.39 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                        )}
                      </svg>
                    </button>
                  );
                  return (
                    <div key={offset}>
                      <div className="flex items-center justify-between mb-3">
                        {offset === 0 ? <NavArrow dir={-1} /> : <span className="w-8" />}
                        <span className="text-sm font-semibold text-gray-800">
                          {MONTHS[m.getMonth()]} {m.getFullYear()}
                        </span>
                        {offset === 1 ? <NavArrow dir={1} /> : <span className="w-8" />}
                      </div>
                      <MonthGrid
                        year={m.getFullYear()}
                        month={m.getMonth()}
                        from={draftFrom}
                        to={draftTo}
                        onDayClick={onDayClick}
                      />
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-end gap-3 mt-5">
                <button
                  type="button"
                  onClick={() => { setDraftFrom(null); setDraftTo(null); }}
                  className="h-10 px-5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={apply}
                  className="h-10 px-5 rounded-lg bg-blue-chill text-white text-sm font-semibold hover:opacity-90 focus:outline-none"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
